<script setup lang="ts">
import { computed } from "vue";
import type { ISatSetupProps } from "@chatfood/design-system";
import { SatSetup } from "@chatfood/design-system";
import { bemBuilder } from "@chatfood/core-utils";
import { useNavigation } from "~/composables/use-navigation";
import { useBranding } from "~/composables/use-branding";
import { useLocale } from "~/composables/use-locale";
import { useAppURI } from "~/composables/use-app-uri";
import { ModalHub } from "~/components/modal-hub";
import { CheckoutCallback } from "~/components/checkout-callback";
import { NuxtRoutedPage } from "~/components/nuxt-routed-page";
import { CookiesConsent } from "~/components/cookies-consent";
import { ReviewForm } from "~/components/review-form";
import { useBusinessStore } from "~/stores/business";
import type ILocaleKeys from "@/i18n/locales/en.json";
import { onMounted, useAnalytics, useHead } from "#imports";

const css = bemBuilder("app");

const business = useBusinessStore();

const { setDefaultLanguage, extractTranslations } = useLocale();
const { getHeadTags } = useBranding();
const { route, rawReplace } = useNavigation();

setDefaultLanguage(business.locale, business.languages);

useHead(
  getHeadTags(
    business.designTokens,
    business.logo,
    business.styles?.customCssSheet,
  ),
);

const isCalorieUnitCal = computed(() =>
  ["us", "ca"].includes(business.country.toLowerCase()),
);

const satelliteLocale = computed<ISatSetupProps["locale"]>(() => {
  const translations = extractTranslations<
    typeof ILocaleKeys.app.satellite_defaults
  >("app.satellite_defaults");

  return {
    ...translations,
    item_handler: {
      ...translations.item_handler,
      calories_value: isCalorieUnitCal.value
        ? translations.item_handler.calories_value_cal
        : translations.item_handler.calories_value,
      info: {
        ...translations.item_handler.info,
        calories_value: isCalorieUnitCal.value
          ? translations.item_handler.info.calories_value_cal
          : translations.item_handler.info.calories_value,
        range_calories_value: isCalorieUnitCal.value
          ? translations.item_handler.info.range_calories_value_cal
          : translations.item_handler.info.range_calories_value,
      },
    },
  };
});

onMounted(() => {
  if (business.isMobileApp) {
    document.documentElement.style.setProperty("--overscroll-behavior", "none");
  }

  useAppURI().persist();
  useAnalytics().trackEvent({
    name: "startJourney",
    payload: {},
  });

  const cleanQuery = { ...route.value.query };
  delete cleanQuery.accessToken;

  rawReplace({
    path: route.value.path,
    query: cleanQuery,
    hash: route.value.hash,
  });
});
</script>

<template>
  <SatSetup :locale="satelliteLocale">
    <CheckoutCallback />
    <div :class="css()">
      <NuxtLayout>
        <NuxtRoutedPage />
      </NuxtLayout>
      <ModalHub />
      <CookiesConsent />
      <ReviewForm v-if="business.id" :business-id="business.id" />
    </div>
  </SatSetup>
</template>

<style lang="scss">
html,
body {
  /* to disable the overscroll bounce feature of browsers */
  overscroll-behavior: var(--overscroll-behavior, initial);
  -webkit-overflow-scrolling: auto;
  min-width: 320px;
  width: 100%;
}
</style>

<style lang="scss" scoped>
.app {
  min-width: 320px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  white-space: pre-line;

  @media (width <= $breakpoint-sm) {
    background-color: var(--color-base-100);
  }
}
</style>
